import {
  Conversation,
  ConversationsData,
  Id,
  MessagesData,
  Timestamp,
} from '@nextforce/api'
import { QueryClient } from '@tanstack/react-query'
import keys from 'chat/shared/queries/keys'
import { produce } from 'immer'

export async function updateConversationLastViewedTimestamp(
  client: QueryClient,
  id: string,
  lastViewedTimestamp: Timestamp
) {
  const conversationsData = client.getQueryData<ConversationsData>(
    keys.conversations()
  )
  const conversationData = client.getQueryData<Conversation>(
    keys.conversation(id)
  )

  if (!conversationsData || !conversationData) {
    return
  }

  await client.cancelQueries({ queryKey: keys.conversations(), exact: true })
  await client.cancelQueries({ queryKey: keys.conversation(id), exact: true })
  client.setQueryData<ConversationsData>(
    keys.conversations(),
    produce(conversationsData, updatedData => {
      const conversation = updatedData.find(c => c.id === id)
      if (conversation) {
        conversation.status = { ...conversation.status, lastViewedTimestamp }
      }
    })
  )
  client.setQueryData<Conversation>(
    keys.conversation(id),
    produce(conversationData, updatedData => {
      const conversation = updatedData
      if (conversation) {
        conversation.status = { ...conversation.status, lastViewedTimestamp }
      }
    })
  )
}

export async function addConversationToCacheIfNeeded(
  client: QueryClient,
  conversation: Conversation
) {
  const key = keys.conversations()
  const data = client.getQueryData<ConversationsData>(key)

  if (!data) {
    return
  }

  const isDuplicate = data.some(o => o.id === conversation.id)

  if (isDuplicate) {
    return
  }

  await client.cancelQueries({ queryKey: key, exact: true })
  client.setQueryData<ConversationsData>(
    key,
    produce(data, updatedData => {
      updatedData.push(conversation)
    })
  )
}

export async function removeConversationFromCache(client: QueryClient, id: Id) {
  const key = keys.conversations()
  const data = client.getQueryData<ConversationsData>(key)

  if (!data) {
    return
  }

  const index = data.findIndex(c => c.id === id)

  if (index < 0) {
    return
  }

  await client.cancelQueries({ queryKey: key, exact: true })
  client.setQueryData<ConversationsData>(
    key,
    produce(data, updatedData => {
      updatedData.splice(index, 1)
    })
  )

  const messagesKey = keys.messages()
  const messagesData = client.getQueryData<MessagesData>(messagesKey)

  if (!messagesData) {
    return
  }

  await client.cancelQueries({ queryKey: messagesKey, exact: true })
  client.setQueryData<MessagesData>(
    messagesKey,
    produce(messagesData, updatedData => {
      updatedData.messages = updatedData.messages.filter(
        m => m.conversationId !== id
      )
    })
  )
}

export async function updateConversationsCache(
  client: QueryClient,
  updatedConversation: Conversation,
  id: Id
) {
  const key = keys.conversations()
  const data = client.getQueryData<ConversationsData>(key)

  if (!data) {
    return
  }

  const index = data.findIndex(c => c.id === id)

  if (index < 0) {
    return
  }

  await client.cancelQueries({ queryKey: key, exact: true })
  client.setQueryData<ConversationsData>(
    key,
    produce(data, updatedData => {
      updatedData[index] = updatedConversation
    })
  )
}

export async function updateDetailsCache(
  client: QueryClient,
  updatedConversation: Conversation,
  id: Id
) {
  const key = keys.conversation(id)

  if (!client.getQueryData<ConversationsData>(key)) {
    return
  }

  await client.cancelQueries({ queryKey: key, exact: true })
  client.setQueryData(key, updatedConversation)
}
